/**
 * Settings page (KNOWN_ISSUES §1.46).
 *
 * Canonical home for the user-controlled preferences this app has
 * accumulated:
 *   • Appearance (theme — §1.45)
 *   • Sound (mute + volume — §"Sound mute persisted")
 *   • Notifications (turn alert toggle — §1.44)
 *   • Account (logout, logout everywhere, export data, delete
 *     account — §1.25 / §1.38)
 *
 * Why this page exists: each preference grew up next to the feature
 * that introduced it (theme on game header, mute via sound icon,
 * notify on profile, GDPR on profile danger zone). Users don't know
 * to look in five different places — `/settings` is the one place
 * they'll think to check.
 *
 * The Profile page keeps the **stats / missions** view (its
 * core purpose) and gains a "Settings" link; the Account section
 * here references back to it for the danger-zone actions to keep
 * the destructive UI in one place.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/contexts/AuthContext";
import { useSound } from "@/contexts/SoundContext";
import { usePageTitle } from "@/hooks/usePageTitle";
import { SUPPORTED_LANGUAGES } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { toast } from "@/components/ui/sonner";
import {
  ArrowLeft, Sun, Moon, Monitor, Volume2, VolumeX,
  Bell, BellOff, ShieldOff, User, ShieldCheck, Copy, X, Settings as SettingsIcon,
} from "lucide-react";
import { Input } from "@/components/ui/input";

import {
  isTurnNotifyEnabled,
  requestTurnNotifyPermission,
  setTurnNotifyEnabled,
} from "@/hooks/useTurnNotifications";
import { authService, formatDetail } from "@/services";
import AccountNavMenu from "@/components/AccountNavMenu";


export default function SettingsPage() {
  const { t, i18n } = useTranslation();
  usePageTitle(t("settings.title"));
  const { user } = useAuth();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const { muted, setMuted, volume, setVolume } = useSound();
  const [turnNotify, setTurnNotify] = useState(() => isTurnNotifyEnabled());
  const [loggingOutAll, setLoggingOutAll] = useState(false);

  // 2FA enrollment / disable flow — KNOWN_ISSUES §1.49.
  // `twoFAEnabled` mirrors the user record so the section toggles
  // between Enable / Disable. The enrollment step lives in a modal.
  const [twoFAEnabled, setTwoFAEnabled] = useState(Boolean(user?.two_fa_enabled));
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [enrollData, setEnrollData] = useState(null); // { qr_data_url, secret, otpauth_uri }
  const [enrollCode, setEnrollCode] = useState("");
  const [enrollError, setEnrollError] = useState("");
  const [enrollLoading, setEnrollLoading] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null); // shown once
  const [disablePassword, setDisablePassword] = useState("");
  const [disableOpen, setDisableOpen] = useState(false);
  const [disableLoading, setDisableLoading] = useState(false);
  const [disableError, setDisableError] = useState("");

  useEffect(() => {
    if (!user) navigate("/login");
  }, [user, navigate]);

  useEffect(() => {
    setTwoFAEnabled(Boolean(user?.two_fa_enabled));
  }, [user?.two_fa_enabled]);

  if (!user) return null;

  // ── Appearance ───────────────────────────────────────────────────────────
  const themeOptions = [
    { value: "light", icon: Sun, label: t("settings.appearance.light") },
    { value: "dark", icon: Moon, label: t("settings.appearance.dark") },
    { value: "system", icon: Monitor, label: t("settings.appearance.system") },
  ];

  // ── Notifications ────────────────────────────────────────────────────────
  const handleEnableTurnNotify = async () => {
    const result = await requestTurnNotifyPermission();
    if (result === "granted") {
      setTurnNotify(true);
      toast.success(t("settings.notifications.enabled"));
    } else if (result === "denied") {
      toast.error(t("settings.notifications.blockedTitle"), {
        description: t("settings.notifications.blockedDescription"),
      });
    } else if (result === "unsupported") {
      toast.error(t("settings.notifications.unsupported"));
    } else {
      toast.info(t("settings.notifications.dismissed"));
    }
  };
  const handleDisableTurnNotify = () => {
    setTurnNotifyEnabled(false);
    setTurnNotify(false);
    toast.success(t("settings.notifications.disabled"));
  };

  // ── Account ──────────────────────────────────────────────────────────────
  const handleLogoutEverywhere = async () => {
    if (loggingOutAll) return;
    const confirmed = window.confirm(
      t("settings.account.logoutEverywhereConfirm"),
    );
    if (!confirmed) return;
    setLoggingOutAll(true);
    try {
      await authService.logoutEverywhere();
      navigate("/login");
    } finally {
      setLoggingOutAll(false);
    }
  };

  // ── 2FA — KNOWN_ISSUES §1.49 ─────────────────────────────────────────────
  const handleStartEnroll = async () => {
    setEnrollError("");
    setEnrollCode("");
    setRecoveryCodes(null);
    try {
      const data = await authService.twoFAEnrollStart();
      setEnrollData(data);
      setEnrollOpen(true);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || t("settings.security.errors.couldNotStart"));
    }
  };

  const handleVerifyEnroll = async (e) => {
    e.preventDefault();
    setEnrollLoading(true);
    setEnrollError("");
    try {
      const data = await authService.twoFAEnrollVerify(enrollCode);
      setRecoveryCodes(data?.recovery_codes ?? []);
      setTwoFAEnabled(true);
    } catch (err) {
      setEnrollError(formatDetail(err.response?.data?.detail) || t("login.error.verificationFailed"));
    }
    setEnrollLoading(false);
  };

  const closeEnrollModal = () => {
    setEnrollOpen(false);
    setEnrollData(null);
    setEnrollCode("");
    setEnrollError("");
    setRecoveryCodes(null);
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    setDisableLoading(true);
    setDisableError("");
    try {
      await authService.twoFADisable(disablePassword);
      setTwoFAEnabled(false);
      setDisableOpen(false);
      setDisablePassword("");
      toast.success(t("settings.security.disabledToast"));
    } catch (err) {
      setDisableError(formatDetail(err.response?.data?.detail) || t("settings.security.errors.couldNotDisable"));
    }
    setDisableLoading(false);
  };

  const handleCopyRecoveryCodes = () => {
    if (!recoveryCodes) return;
    navigator.clipboard?.writeText(recoveryCodes.join("\n"));
    toast.success(t("settings.security.recoveryCopied"));
  };

  // Volume slider works in 0–100 scale for the Radix component; the
  // SoundContext stores 0–1.
  const volumePct = Math.round((volume ?? 0.7) * 100);

  return (
    <div className="min-h-screen bg-[#E3F2FD] dark:bg-[#0a1428] p-4 md:p-8" id="main-content" role="main" tabIndex={-1} data-testid="settings-page">
      <div className="max-w-2xl mx-auto">
        {/* Header split: back + menu on top row, title given its own
            row below. Stops the back/title/menu trio from feeling
            crammed on narrow screens. */}
        <div className="flex items-center justify-between gap-3 mb-3">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            data-testid="back-to-dashboard"
            className="rounded-full"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> {t("settings.navigation.back")}
          </Button>
          <AccountNavMenu current="settings" />
        </div>
        <h1
          className="text-2xl md:text-3xl font-bold text-[#1A237E] dark:text-[#E3F2FD] flex items-center gap-2 mb-6"
          style={{ fontFamily: "Fredoka" }}
        >
          <SettingsIcon className="w-6 h-6 text-[#38B6FF] shrink-0" />
          {t("settings.title")}
        </h1>

        {/* Appearance */}
        <section
          className="bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-2 border-white/40 shadow-xl rounded-3xl p-6 mb-6"
          data-testid="settings-appearance"
        >
          <h2 className="text-lg font-bold text-[#1A237E] dark:text-[#E3F2FD] mb-1" style={{ fontFamily: "Fredoka" }}>
            {t("settings.appearance.title")}
          </h2>
          <p className="text-xs text-[#1A237E]/60 dark:text-[#E3F2FD]/60 mb-4">
            {t("settings.appearance.description")}
          </p>
          <div className="inline-flex rounded-xl border-2 border-[#1A237E]/10 dark:border-[#E3F2FD]/10 overflow-hidden" role="radiogroup" aria-label={t("settings.appearance.themeLabel")}>
            {themeOptions.map(({ value, icon: Icon, label }) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={theme === value}
                onClick={() => setTheme(value)}
                data-testid={`settings-theme-${value}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition-colors ${
                  theme === value
                    ? "bg-[#38B6FF] text-white"
                    : "bg-white/60 dark:bg-slate-900/60 text-[#1A237E]/60 dark:text-[#E3F2FD]/60 hover:bg-[#38B6FF]/10"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </div>
        </section>

        {/* Sound */}
        <section
          className="bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-2 border-white/40 shadow-xl rounded-3xl p-6 mb-6"
          data-testid="settings-sound"
        >
          <h2 className="text-lg font-bold text-[#1A237E] dark:text-[#E3F2FD] mb-1" style={{ fontFamily: "Fredoka" }}>
            {t("settings.sound.title")}
          </h2>
          <p className="text-xs text-[#1A237E]/60 dark:text-[#E3F2FD]/60 mb-4">
            {t("settings.sound.description")}
          </p>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => setMuted(!muted)}
              className="rounded-xl"
              data-testid="settings-sound-toggle"
              aria-pressed={muted}
              aria-label={muted ? t("settings.sound.unmuteLabel") : t("settings.sound.muteLabel")}
            >
              {muted ? <VolumeX className="w-4 h-4 mr-2" /> : <Volume2 className="w-4 h-4 mr-2" />}
              {muted ? t("settings.sound.unmute") : t("settings.sound.mute")}
            </Button>
            <div className="flex-1 max-w-xs">
              <Slider
                min={0}
                max={100}
                step={1}
                value={[volumePct]}
                onValueChange={(v) => setVolume((v[0] ?? 70) / 100)}
                disabled={muted}
                aria-label={t("settings.sound.volumeLabel")}
                data-testid="settings-volume-slider"
              />
            </div>
            <span
              className="text-xs font-semibold tabular-nums text-[#1A237E]/60 dark:text-[#E3F2FD]/60 w-10 text-right"
              data-testid="settings-volume-value"
            >
              {volumePct}%
            </span>
          </div>
        </section>

        {/* Notifications */}
        <section
          className="bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-2 border-white/40 shadow-xl rounded-3xl p-6 mb-6"
          data-testid="settings-notifications"
        >
          <h2 className="text-lg font-bold text-[#1A237E] dark:text-[#E3F2FD] mb-1" style={{ fontFamily: "Fredoka" }}>
            {t("settings.notifications.title")}
          </h2>
          <p className="text-xs text-[#1A237E]/60 dark:text-[#E3F2FD]/60 mb-4">
            {t("settings.notifications.description")}
          </p>
          {turnNotify ? (
            <Button
              variant="outline"
              onClick={handleDisableTurnNotify}
              className="rounded-xl"
              data-testid="settings-turn-notify-disable"
            >
              <BellOff className="w-4 h-4 mr-2" />
              {t("settings.notifications.disable")}
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={handleEnableTurnNotify}
              className="rounded-xl"
              data-testid="settings-turn-notify-enable"
            >
              <Bell className="w-4 h-4 mr-2" />
              {t("settings.notifications.enable")}
            </Button>
          )}
        </section>

        {/* Language — KNOWN_ISSUES §1.48 */}
        <section
          className="bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-2 border-white/40 shadow-xl rounded-3xl p-6 mb-6"
          data-testid="settings-language"
        >
          <h2 className="text-lg font-bold text-[#1A237E] dark:text-[#E3F2FD] mb-1" style={{ fontFamily: "Fredoka" }}>
            {t("settings.language.title")}
          </h2>
          <p className="text-xs text-[#1A237E]/60 dark:text-[#E3F2FD]/60 mb-4">
            {t("settings.language.description")}
          </p>
          <div className="inline-flex rounded-xl border-2 border-[#1A237E]/10 dark:border-[#E3F2FD]/10 overflow-hidden" role="radiogroup" aria-label={t("settings.language.label")}>
            {SUPPORTED_LANGUAGES.map(({ code, nativeName }) => (
              <button
                key={code}
                type="button"
                role="radio"
                aria-checked={i18n.resolvedLanguage === code}
                onClick={() => i18n.changeLanguage(code)}
                data-testid={`settings-lang-${code}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition-colors ${
                  i18n.resolvedLanguage === code
                    ? "bg-[#38B6FF] text-white"
                    : "bg-white/60 dark:bg-slate-900/60 text-[#1A237E]/60 dark:text-[#E3F2FD]/60 hover:bg-[#38B6FF]/10"
                }`}
              >
                {nativeName}
              </button>
            ))}
          </div>
        </section>

        {/* Security — 2FA (KNOWN_ISSUES §1.49) */}
        <section
          className="bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-2 border-white/40 shadow-xl rounded-3xl p-6 mb-6"
          data-testid="settings-security"
        >
          <h2 className="text-lg font-bold text-[#1A237E] dark:text-[#E3F2FD] mb-1" style={{ fontFamily: "Fredoka" }}>
            {t("settings.security.title")}
          </h2>
          <p className="text-xs text-[#1A237E]/60 dark:text-[#E3F2FD]/60 mb-4">
            {t("settings.security.description")}
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                twoFAEnabled
                  ? "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200"
                  : "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
              }`}
              data-testid="settings-2fa-status"
            >
              <ShieldCheck className="w-3.5 h-3.5" />
              {twoFAEnabled ? t("settings.security.enabled") : t("settings.security.notEnabled")}
            </span>
            {twoFAEnabled ? (
              <Button
                variant="outline"
                onClick={() => {
                  setDisableOpen(true);
                  setDisablePassword("");
                  setDisableError("");
                }}
                className="rounded-xl"
                data-testid="settings-2fa-disable-btn"
              >
                {t("settings.security.disable")}
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={handleStartEnroll}
                className="rounded-xl"
                data-testid="settings-2fa-enable-btn"
              >
                {t("settings.security.enable")}
              </Button>
            )}
          </div>
        </section>

        {/* Account */}
        <section
          className="bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-2 border-white/40 shadow-xl rounded-3xl p-6"
          data-testid="settings-account"
        >
          <h2 className="text-lg font-bold text-[#1A237E] dark:text-[#E3F2FD] mb-1" style={{ fontFamily: "Fredoka" }}>
            {t("settings.account.title")}
          </h2>
          <p className="text-xs text-[#1A237E]/60 dark:text-[#E3F2FD]/60 mb-4">
            {t("settings.account.signedInAs")} <span className="font-semibold text-[#1A237E] dark:text-[#E3F2FD]">{user.email}</span>. <button
              type="button"
              onClick={() => navigate("/profile")}
              className="underline text-[#38B6FF] hover:text-[#2196F3]"
            >{t("settings.account.dangerZoneLink")}</button>.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => navigate("/profile")}
              className="rounded-xl"
              data-testid="settings-go-to-profile"
            >
              <User className="w-4 h-4 mr-2" />
              {t("settings.account.goToProfile")}
            </Button>
            <Button
              variant="outline"
              onClick={handleLogoutEverywhere}
              disabled={loggingOutAll}
              className="rounded-xl"
              data-testid="settings-logout-all"
            >
              <ShieldOff className="w-4 h-4 mr-2" />
              {loggingOutAll ? t("settings.account.loggingOutEverywhere") : t("settings.account.logoutEverywhere")}
            </Button>
          </div>
        </section>
      </div>

      {/* 2FA Enrollment modal — two-step: scan QR / verify code, then
          show recovery codes once. */}
      {enrollOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="enroll-2fa-title"
          data-testid="enroll-2fa-modal"
        >
          <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 id="enroll-2fa-title" className="text-lg font-bold text-[#1A237E] dark:text-[#E3F2FD]" style={{ fontFamily: "Fredoka" }}>
                {recoveryCodes ? t("settings.security.enrollModal.recoveryCodes.title") : t("settings.security.enrollModal.title")}
              </h3>
              <button
                type="button"
                onClick={closeEnrollModal}
                aria-label={t("settings.security.enrollModal.close")}
                className="text-[#1A237E]/60 dark:text-[#E3F2FD]/60 hover:text-[#1A237E]"
                data-testid="enroll-2fa-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {!recoveryCodes && enrollData ? (
              <>
                <p className="text-sm text-[#1A237E]/70 dark:text-[#E3F2FD]/70 mb-3">
                  {t("settings.security.enrollModal.scanInstructions")}
                </p>
                <div className="flex justify-center mb-3">
                  <img
                    src={enrollData.qr_data_url}
                    alt={t("settings.security.enrollModal.qrAlt")}
                    className="w-48 h-48 bg-white p-2 rounded-xl border"
                    data-testid="enroll-2fa-qr"
                  />
                </div>
                <p className="text-xs text-[#1A237E]/60 dark:text-[#E3F2FD]/60 mb-1">
                  {t("settings.security.enrollModal.manualKey")}
                </p>
                <code
                  className="block text-xs font-mono bg-[#1A237E]/5 dark:bg-[#E3F2FD]/5 rounded p-2 mb-4 break-all select-all"
                  data-testid="enroll-2fa-secret"
                >
                  {enrollData.secret}
                </code>
                <form onSubmit={handleVerifyEnroll} className="space-y-3">
                  <p className="text-sm text-[#1A237E]/70 dark:text-[#E3F2FD]/70">
                    {t("settings.security.enrollModal.verifyInstructions")}
                  </p>
                  <Input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder={t("login.twoFA.placeholder")}
                    aria-label={t("settings.security.enrollModal.verificationCode")}
                    value={enrollCode}
                    onChange={(e) => setEnrollCode(e.target.value)}
                    className="text-center font-bold text-lg tracking-widest"
                    maxLength={10}
                    required
                    autoFocus
                    data-testid="enroll-2fa-code-input"
                  />
                  {enrollError && (
                    <p className="text-red-500 text-sm" data-testid="enroll-2fa-error">{enrollError}</p>
                  )}
                  <Button
                    type="submit"
                    disabled={enrollLoading || !enrollCode.trim()}
                    className="w-full rounded-full bg-[#38B6FF] hover:bg-[#2196F3] text-white font-bold"
                    data-testid="enroll-2fa-verify-btn"
                  >
                    {enrollLoading ? t("settings.security.enrollModal.button.verifying") : t("settings.security.enrollModal.button.verify")}
                  </Button>
                </form>
              </>
            ) : recoveryCodes ? (
              <>
                <p className="text-sm text-amber-700 dark:text-amber-300 font-semibold mb-2">
                  {t("settings.security.enrollModal.recoveryCodes.warning")}
                </p>
                <p className="text-xs text-[#1A237E]/60 dark:text-[#E3F2FD]/60 mb-3">
                  {t("settings.security.enrollModal.recoveryCodes.description")}
                </p>
                <div
                  className="grid grid-cols-2 gap-2 bg-[#1A237E]/5 dark:bg-[#E3F2FD]/5 rounded-xl p-3 mb-4 font-mono text-sm"
                  data-testid="enroll-2fa-recovery-codes"
                >
                  {recoveryCodes.map((code) => (
                    <span key={code} className="select-all">{code}</span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handleCopyRecoveryCodes}
                    className="flex-1 rounded-full"
                    data-testid="enroll-2fa-copy-btn"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    {t("settings.security.enrollModal.button.copy")}
                  </Button>
                  <Button
                    onClick={closeEnrollModal}
                    className="flex-1 rounded-full bg-[#38B6FF] hover:bg-[#2196F3] text-white font-bold"
                    data-testid="enroll-2fa-done-btn"
                  >
                    {t("settings.security.enrollModal.button.done")}
                  </Button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {/* 2FA Disable modal — password reconfirm so a hijacked session
          can't unilaterally drop the second factor. */}
      {disableOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="disable-2fa-title"
          data-testid="disable-2fa-modal"
        >
          <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 id="disable-2fa-title" className="text-lg font-bold text-[#1A237E] dark:text-[#E3F2FD]" style={{ fontFamily: "Fredoka" }}>
                {t("settings.security.disableModal.title")}
              </h3>
              <button
                type="button"
                onClick={() => setDisableOpen(false)}
                aria-label={t("settings.security.enrollModal.close")}
                className="text-[#1A237E]/60 dark:text-[#E3F2FD]/60 hover:text-[#1A237E]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-[#1A237E]/70 dark:text-[#E3F2FD]/70 mb-3">
              {t("settings.security.disableModal.description")}
            </p>
            <form onSubmit={handleDisable} className="space-y-3">
              <Input
                type="password"
                autoComplete="current-password"
                placeholder={t("settings.security.disableModal.passwordLabel")}
                aria-label={t("settings.security.disableModal.passwordLabel")}
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                required
                autoFocus
                data-testid="disable-2fa-password-input"
              />
              {disableError && (
                <p className="text-red-500 text-sm" data-testid="disable-2fa-error">{disableError}</p>
              )}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDisableOpen(false)}
                  className="flex-1 rounded-full"
                >
                  {t("settings.security.disableModal.button.cancel")}
                </Button>
                <Button
                  type="submit"
                  disabled={disableLoading || !disablePassword}
                  className="flex-1 rounded-full bg-red-500 hover:bg-red-600 text-white font-bold"
                  data-testid="disable-2fa-confirm-btn"
                >
                  {disableLoading ? t("settings.security.disableModal.button.disabling") : t("settings.security.disableModal.button.confirm")}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
